import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BookOpen, Award, FileDown } from "lucide-react";
import { logIA } from "@/lib/iaLogger";

const downloadPdf = (url, filename) => {
  const token = localStorage.getItem("mazid_token");
  fetch(`${process.env.REACT_APP_BACKEND_URL}${url}`, { headers: { Authorization: `Bearer ${token}` } })
    .then(r => r.blob())
    .then(blob => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    });
};

export default function GradesPage() {
  const [grades, setGrades] = useState([]);
  const [students, setStudents] = useState([]);
  const [bulletins, setBulletins] = useState([]);

  useEffect(() => {
    logIA("view_result");
    Promise.all([api.get("/grades"), api.get("/students")]).then(([g, s]) => {
      setGrades(g.data);
      setStudents(s.data);
    });
    api.get("/pedagogy/bulletins").then(r => setBulletins(r.data)).catch(() => {});
  }, []);

  const studentMap = Object.fromEntries(students.map(s => [s.id, s]));

  // Group grades by student then by period
  const grouped = {};
  grades.forEach(g => {
    if (!grouped[g.student_id]) grouped[g.student_id] = {};
    if (!grouped[g.student_id][g.period]) grouped[g.student_id][g.period] = [];
    grouped[g.student_id][g.period].push(g);
  });

  return (
    <div className="space-y-5" data-testid="page-grades">
      <h2 className="font-heading text-3xl font-extrabold text-[#1E3A8A] tracking-tight">Notes & bulletins</h2>

      {/* Bulletins officiels (générés à la validation des notes) */}
      {bulletins.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="official-bulletins">
          <div className="font-heading font-bold text-lg text-[#1E3A8A] mb-3 flex items-center gap-2">
            <FileDown className="h-5 w-5" />Bulletins officiels
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {bulletins.map(b => (
              <button key={b.id}
                onClick={() => downloadPdf(`/api/pedagogy/bulletins/${b.id}/pdf`, `bulletin-${b.student_name}-${b.period}.pdf`)}
                className="border border-[#1E3A8A]/30 rounded-lg p-3 text-left hover:bg-blue-50 transition flex items-center justify-between gap-2"
                data-testid={`bulletin-dl-${b.id}`}>
                <div>
                  <div className="text-sm font-semibold text-[#0F172A]">{b.student_name}</div>
                  <div className="text-xs text-slate-500">{b.period} · {b.class_name}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">Validé le {new Date(b.generated_at).toLocaleDateString("fr-FR")}</div>
                </div>
                <FileDown className="h-4 w-4 text-[#1E3A8A] shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}

      {Object.keys(grouped).length === 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-500">Aucune note enregistrée pour le moment.</div>
      )}

      {Object.entries(grouped).map(([sid, periods]) => {
        const student = studentMap[sid];
        return (
          <div key={sid} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`grades-student-${sid}`}>
            <div className="bg-gradient-to-r from-[#1E3A8A] to-[#166534] text-white p-5">
              <div className="font-heading font-bold text-xl">{student ? `${student.first_name} ${student.last_name}` : "Élève"}</div>
              <div className="text-sm opacity-80">{student?.class_name}</div>
            </div>
            <div className="p-5 space-y-5">
              {Object.entries(periods).map(([period, grades]) => {
                const avg = grades.reduce((s, g) => s + g.value, 0) / grades.length;
                return (
                  <div key={period}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="chip"><BookOpen className="h-3 w-3" /><span>{period}</span></div>
                      <div className="flex items-center gap-2">
                        <Award className="h-4 w-4 text-[#FBBF24]" />
                        <span className="text-sm font-semibold">Moyenne: <span className="text-[#166534] font-bold">{avg.toFixed(2)}/20</span></span>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-3">
                      {grades.map((g) => (
                        <div key={g.id} className="border border-slate-200 rounded-lg p-3 flex items-center justify-between" data-testid={`grade-${g.id}`}>
                          <div>
                            <div className="text-sm font-semibold text-[#0F172A]">{g.subject}</div>
                            {g.comment && <div className="text-xs text-slate-500 mt-0.5">{g.comment}</div>}
                          </div>
                          <div className={`font-heading font-extrabold text-xl ${g.value >= 14 ? "text-[#166534]" : g.value >= 10 ? "text-[#FBBF24]" : "text-[#EA580C]"}`}>
                            {g.value}<span className="text-sm text-slate-400">/{g.max_value}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
